import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { bulkDeleteDocuments } from '../../actions/dms';
import { documentActions, modalActions } from '../../actions';
import { userOptionObj } from '../../utils';
import { MyDropdown, ChevronDown } from '../MyDropdown';
import { documentConstants } from '../../constants';
import { isUserRestricted } from '../../helpers/permissionHelper';

import ProgressBar from '../ProgressBar';

const selectStyles = {
  control: provided => ({
    ...provided,
    display: 'none',
  }),
  option: (styles, { data }) => {
    return {
      ...styles,
      color: data?.value === 'delete_documents' ||  data?.value === 'delete_invoices'
        ? '#FF0000'
        : '#353535',
    };
  },
  menu: () => ({
    boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)',
    width: "max-content",
    minWidth: "100%"
  }),
  menuList: () => ({
    minHeight: 'fit-content',
  })
};

const BulkActionBar = ({
  documents,
  dispatch,
  selectedItemIds,
  setSelectedItemIds,
  setAllSelected,
  setReload,
  currentUser,
  viewType,
}) => {
  const { status, exportedPercent } = useSelector(state => state.dms.exportDocuments);

  const clearAllSelection = () => {
    setAllSelected(false);
    setSelectedItemIds([]);
  };

  useEffect(() => {
    if (status === documentConstants.EXPORT_STATUSES.COMPLETED) {
      clearAllSelection();
    }
  }, [status]);

  if (selectedItemIds?.length < 1 || currentUser?.isSupport) return null;
  const isInvoice = viewType === 'invoices';
  const selectedItems = documents.filter(item => selectedItemIds.includes(item.id));
  const invoice_approval_v2 = documents?.[0]?.invoice_approval_v2;

  const deleteBulk = () => {
    dispatch(bulkDeleteDocuments(selectedItems, setReload));
  };

  const exportBulk = () => {
    dispatch(documentActions.downloadAndZipDocuments(selectedItems));
  };

  const updateBulk = () => {
    dispatch(modalActions.showModal('BULK_UPDATE_CATEGORY', { selectedItems, setReload }));
  };

  const renameBulk = () => {
    dispatch(modalActions.showModal('BULK_APPLY_SUGGESTED_NAME', { selectedItems, setReload }));
  };

  const bulkApproveInvoices = () => {
    dispatch(modalActions.showModal('BULK_APPROVE_INVOICE', { selectedItems, setReload }));
  };

  const bulkChangeInvoicePriority = newPriority => {
    dispatch(modalActions.showModal('BULK_UPDATE_PRIORITY', { selectedItems, setReload, newPriority }));
  };

  const transferInvoiceAssignment = () => {
    dispatch(modalActions.showModal('BULK_TRANSFER_INVOICE_ASSIGNMENT', { selectedItems, setReload }));
  };

  const bulkDropdownOptions = () => {
    const canDeleteDocument = !isUserRestricted(currentUser.role, 'document.delete') && currentUser?.document_permissions?.['document.delete'];
    const canDeleteInvoice = !isUserRestricted(currentUser.role, 'invoice.delete') && currentUser?.document_permissions?.['invoice.delete'];
    const canUpdateDocument =
      currentUser?.isOrganisationAdmin || (currentUser?.organisation_id && currentUser?.document_permissions?.['invoice.override']);
    const canBulkExport = !currentUser?.isGuestUser;

    const options = canBulkExport ? [{ label: `Export ${selectedItemIds.length} ${viewType}`, value: 'export', onClick: exportBulk }] : [];

    if (!isInvoice && canUpdateDocument) {
      options.push({ label: `Change category of ${selectedItemIds.length} documents`, value: 'change_documents_category', onClick: updateBulk });
      options.push({ label: `Change name of ${selectedItemIds.length} documents`, value: 'change_names', onClick: renameBulk });
    }

    if (!currentUser?.isContractor && isInvoice) {
      options.push({ label: `Approve ${selectedItemIds.length} ${viewType}`, value: 'approve_invoices', onClick: bulkApproveInvoices });
    }

    if (!currentUser?.isContractor && isInvoice) {
      options.push({
        label: `Remind Action Users of ${selectedItemIds.length} documents`,
        value: 'remind_invoice_action_users',
        onClick: showBulkRemindActionUsersModal,
      });
      options.push({
        label: `Add priority to ${selectedItemIds.length} invoices`,
        value: 'add_priority',
        onClick: () => bulkChangeInvoicePriority(true),
      });
      options.push({
        label: `Remove priority from ${selectedItemIds.length} invoices`,
        value: 'remove_priority',
        onClick: () => bulkChangeInvoicePriority(false),
      });
    }

    if (isInvoice && invoice_approval_v2 && (currentUser?.isOrganisationAdmin || currentUser?.isStrataManager)) {
      options.push({
        label: `Transfer assignment of ${selectedItemIds.length} invoice(s)`,
        value: 'transfer_invoice_assignment',
        onClick: transferInvoiceAssignment,
      });
    }

    if (isInvoice && canDeleteInvoice) {
      options.push({ label: `Delete ${selectedItemIds.length} invoices`, value: 'delete_invoices', onClick: deleteBulk });
    }

    if (!isInvoice && canDeleteDocument) {
      options.push({ label: `Delete ${selectedItemIds.length} documents`, value: 'delete_documents', onClick: deleteBulk });
    }

    return options;
  };

  const showBulkRemindActionUsersModal = async () => {
    const currentlyWithUser = selectedItems?.map(item => item.assigned_attachment_approvals.map(i => userOptionObj(i.user)))?.flat();

    dispatch(
      modalActions.showModal('REMIND_ACTION_USER', {
        bulkRemind: true,
        currentlyWithUser,
        selectedItems,
      })
    );
  };

  return (
    <div className="bulk-actions-bar-container">
      <div className="bulk-actions-bar">
        <div className="bulk-actions-bar--left">
          <button
            type="button"
            className="icon icon-cross-red bulk-actions-bar__clear-button"
            onClick={clearAllSelection}
          />
          <strong>{selectedItemIds.length} items selected</strong>
        </div>

        <div className="bulk-actions-bar--right">
          <MyDropdown
            dropdownProps={{ dropdownClassName: 'bulk-actions-button', dropdownText: 'Bulk Actions', showChevronDown: true }}
            selectProps={{ styles: selectStyles, options: bulkDropdownOptions() }}
            dropdownClassName="bulk-actions-button"
            dropdownText={
              <>
                Bulk Actions <ChevronDown />
              </>
            }
          />
        </div>
      </div>
      {status === documentConstants.EXPORT_STATUSES.RUNNING && (
        <div className="export-status-container">
          <ProgressBar completed={exportedPercent} labelStyles={{ fontSize: '0.9em' }} />
        </div>
      )}
    </div>
  );
};

export default connect(state => state.dms)(BulkActionBar);
